/**
 * React Hook for MusicMatch AI
 */

import { useState, useCallback, useEffect } from 'react';
import { musicMatchService, MusicContext, Track, PlaylistRecommendation } from '@/services/music-match-service';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface UseMusicMatchReturn {
  currentTrack: Track | null;
  playlist: Track[];
  isPlaying: boolean;
  generatePlaylist: (context: MusicContext) => Promise<void>;
  playTrack: (track: Track) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  next: () => Promise<void>;
  previous: () => Promise<void>;
  likeTrack: (track: Track) => Promise<void>;
  dislikeTrack: (track: Track) => Promise<void>;
  adjustPlaylist: (context: MusicContext) => Promise<void>;
}

export function useMusicMatch(
  provider: 'spotify' | 'apple' | 'deezer' = 'spotify',
  spotifyPlayFunction?: (uri: string) => Promise<void>
): UseMusicMatchReturn {
  const { toast } = useToast();
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);

  // Initialize service
  useEffect(() => {
    musicMatchService.initialize(provider);
  }, [provider]);

  // Set Spotify player function if provided
  useEffect(() => {
    if (spotifyPlayFunction) {
      musicMatchService.setSpotifyPlayer(spotifyPlayFunction);
    }
  }, [spotifyPlayFunction]);

  const generatePlaylist = useCallback(async (context: MusicContext) => {
    try {
      const recommendation = await musicMatchService.generatePlaylist(context);
      setPlaylist(recommendation.tracks);
      
      toast({
        title: 'Playlist Pronta',
        description: recommendation.reason,
      });
    } catch (error) {
      logger.error('Failed to generate playlist', error, { hook: 'useMusicMatch', action: 'generatePlaylist', context });
      toast({
        variant: 'destructive',
        title: 'Erro na Playlist',
        description: 'Falha ao gerar playlist',
      });
    }
  }, [toast]);

  const playTrack = useCallback(async (track: Track) => {
    try {
      await musicMatchService.playTrack(track);
      setCurrentTrack(track);
      setIsPlaying(true);
    } catch (error) {
      logger.error('Failed to play track', error, { hook: 'useMusicMatch', action: 'playTrack', trackId: track.id });
    }
  }, []);

  const pause = useCallback(async () => {
    try {
      await musicMatchService.pause();
      setIsPlaying(false);
    } catch (error) {
      logger.error('Failed to pause', error, { hook: 'useMusicMatch', action: 'pause' });
    }
  }, []);

  const resume = useCallback(async () => {
    try {
      await musicMatchService.resume();
      setIsPlaying(true);
    } catch (error) {
      logger.error('Failed to resume', error, { hook: 'useMusicMatch', action: 'resume' });
    }
  }, []);

  const next = useCallback(async () => {
    try {
      const nextTrack = await musicMatchService.next();
      if (nextTrack) {
        setCurrentTrack(nextTrack);
      }
    } catch (error) {
      logger.error('Failed to skip to next', error, { hook: 'useMusicMatch', action: 'next' });
    }
  }, []);

  const previous = useCallback(async () => {
    try {
      const prevTrack = await musicMatchService.previous();
      if (prevTrack) {
        setCurrentTrack(prevTrack);
      }
    } catch (error) {
      logger.error('Failed to skip to previous', error, { hook: 'useMusicMatch', action: 'previous' });
    }
  }, []);

  const likeTrack = useCallback(async (track: Track) => {
    await musicMatchService.likeTrack(track);
  }, []);

  const dislikeTrack = useCallback(async (track: Track) => {
    await musicMatchService.dislikeTrack(track);
    // Update current track after skip
    const nextTrack = musicMatchService.getCurrentTrack();
    setCurrentTrack(nextTrack);
  }, []);

  const adjustPlaylist = useCallback(async (context: MusicContext) => {
    try {
      const newTrack = await musicMatchService.adjustPlaylist(context);
      if (newTrack) {
        await playTrack(newTrack);
        toast({
          title: 'Música Ajustada',
          description: `Tocando agora: ${newTrack.name}`,
        });
      }
    } catch (error) {
      logger.error('Failed to adjust playlist', error, { hook: 'useMusicMatch', action: 'adjustPlaylist', context });
    }
  }, [playTrack, toast]);

  return {
    currentTrack,
    playlist,
    isPlaying,
    generatePlaylist,
    playTrack,
    pause,
    resume,
    next,
    previous,
    likeTrack,
    dislikeTrack,
    adjustPlaylist
  };
}
